import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { getPmImpersonationContext, clearPmImpersonationContext } from '../lib/pmImpersonation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PM_ROLES = ['property_manager', 'asset_manager', 'maintenance_supervisor'];

/**
 * Banner shown across the app when an admin is impersonating another user.
 * Renders a distinct FULL-ACCESS banner for Property Management portal
 * impersonation; the generic "View As" banner is unchanged for every other
 * (customer/VIP/employee/User-Management) impersonation. Renders nothing when
 * not impersonating.
 */
const ImpersonationBanner = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || !user.impersonator) return null;

  const isPm = PM_ROLES.includes(user.role);
  const pmCtx = isPm ? getPmImpersonationContext() : null;
  const companyName = pmCtx?.company || user.pm_company_name || '';

  const stop = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/stop-impersonating`, {}, { withCredentials: true });
      clearPmImpersonationContext();
      toast.success(`Returned to ${user.impersonator.name}`);
      // Full reload so AuthContext and every route guard sees the restored admin cookies.
      window.location.href = isPm ? '/admin/pm/accounts' : '/admin/users';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to stop impersonating');
      setBusy(false);
    }
  };

  if (isPm) {
    return (
      <div
        data-testid="pm-impersonation-banner"
        className="sticky top-0 z-50 bg-[#7C2D12] border-b-2 border-orange-400 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-4 h-4 text-orange-300 shrink-0" />
          <p className="text-xs sm:text-sm truncate">
            <span className="text-orange-300 font-bold tracking-wider uppercase">Admin Impersonation — Full Access</span>{' '}
            <span className="text-orange-100/70 hidden sm:inline">·</span>{' '}
            <span className="text-white font-medium">Acting as {user.name}</span>
            {companyName ? <span className="text-orange-100/90"> for {companyName}</span> : null}
          </p>
        </div>
        <button
          data-testid="pm-impersonation-exit"
          onClick={stop}
          disabled={busy}
          className="shrink-0 bg-white text-[#7C2D12] hover:bg-orange-50 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-60"
        >
          {busy ? 'Exiting…' : 'Return to Admin'}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-[#7F1D1D] border-b-2 border-amber-400 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
    >
      <div className="flex items-center gap-2 min-w-0">
        <ShieldAlert className="w-4 h-4 text-amber-300 shrink-0" />
        <p className="text-xs sm:text-sm truncate">
          <span className="text-amber-300 font-semibold tracking-wider uppercase">View As</span>{' '}
          <span className="text-white font-medium">{user.name}</span>{' '}
          <span className="text-amber-100/70">·</span>{' '}
          <span className="text-amber-100/80">acting as {user.impersonator.name}</span>
        </p>
      </div>
      <button
        data-testid="impersonation-exit"
        onClick={stop}
        disabled={busy}
        className="shrink-0 bg-white text-[#7F1D1D] hover:bg-amber-50 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-60"
      >
        {busy ? 'Exiting…' : 'Return to my account'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
